import React from 'react';
import PropTypes from 'prop-types';

import ContactModal from './integration_modals/contact_modal';
import CalendarModal from './integration_modals/calendar_modal';

import contactIcon from './images/contact_integration.svg';
import calendarIcon from './images/calendar_integration.svg';
import clickToCallIcon from './images/click_to_call.svg';

import IntegrationsApi from './integrations_api';
import AvailableIntegrationsCard from './available_integrations_card';
import ClickToCallRegistrationModal from 'components/click_to_call/registration_modal';

export default class AvailableIntegrations extends React.Component {
  static propTypes = {
    calendarIntegration: PropTypes.shape({
      databaseId: PropTypes.number
    }),
    clickToCallIntegration: PropTypes.object,
    contactIntegration: PropTypes.shape({
      databaseId: PropTypes.number
    }),
    contactSyncAccess: PropTypes.bool,
    currentDatabase: PropTypes.shape({
      databaseId: PropTypes.number
    }).isRequired,
    currentUser: PropTypes.shape({
      calendarIntegrationAvailable: PropTypes.bool,
      clickToCallEnabled: PropTypes.bool
    }).isRequired,
    databases: PropTypes.array.isRequired,
    googleIntegration: PropTypes.object,
    nylas: PropTypes.bool.isRequired,
    paths: PropTypes.object.isRequired
  };

  state = {
    showCalendarModal: false,
    showClickToCallModal: false,
    showContactModal: false
  };

  api = new IntegrationsApi();

  handleShowContactModal = () => this.setState({ showContactModal: true });
  handleShowCalendarModal = () => this.setState({ showCalendarModal: true });
  handleShowClickToCallModal = () => this.setState({ showClickToCallModal: true });
  handleCloseModals = () => this.setState({
    showCalendarModal: false,
    showClickToCallModal: false,
    showContactModal: false
  });

  handleFinishClickToCall = (success) => {
    this.handleCloseModals();

    if (success) {
      window.location.reload();
    }
  };

  handleSaveIntegration = (databaseId, scope) => {
    const { nylas, currentUser } = this.props;
    let beginIntegration;

    if (nylas || scope === 'contacts') {
      beginIntegration = this.api.beginNylasIntegration(databaseId, currentUser.id, scope);
    } else {
      beginIntegration = this.api.beginGoogleIntegration(databaseId, currentUser.id);
    }

    beginIntegration.then((response) => {
      this.handleCloseModals();
      window.location.assign(response.data.redirect_url);
    });
  };

  render() {
    return (
      <React.Fragment>
        <p className="fs-large fw-semibold mt-6 mb-0">Available Integrations</p>
        {this.renderAvailableIntegrations()}
        {this.renderContactModal()}
        {this.renderClickToCallModal()}
        {this.renderCalendarModal()}
      </React.Fragment>
    );
  }

  renderAvailableIntegrations() {
    const { calendarIntegration, clickToCallIntegration, contactIntegration, currentDatabase } = this.props;

    const contactDatabaseId = contactIntegration ? contactIntegration.databaseId : null;
    const calendarDatabaseId = calendarIntegration ? calendarIntegration.databaseId : null;

    if (
      contactDatabaseId === calendarDatabaseId
      && calendarDatabaseId === currentDatabase.id
      && clickToCallIntegration
    ) {
      return (
        <p className="fs-large text-muted mb-0">
          This database is currently using all available integrations
        </p>
      );
    } else {
      return (
        <React.Fragment>
          {this.renderContactBlock()}
          {this.renderCalendarBlock()}
          {this.renderClickToCallBlock()}
        </React.Fragment>
      );
    }
  }

  renderCalendarBlock() {
    const {
      calendarIntegration,
      currentDatabase,
      currentUser: { calendarIntegrationAvailable },
      googleIntegration,
      databases,
      nylas
    } = this.props;
    const calendarInt = calendarIntegration || googleIntegration;
    const databaseId = calendarInt ? calendarInt.databaseId : null;

    if (!calendarIntegrationAvailable || databaseId === currentDatabase.id) return;
    let availableIntegrations = 'Google';

    if (nylas) {
      availableIntegrations = 'Google, Office, ICloud or Yahoo';
    }

    return (
      <AvailableIntegrationsCard
        currentDatabase={currentDatabase}
        databases={databases}
        image={calendarIcon}
        integration={calendarInt}
        name="calendar"
        toolTipId="calendar-tooltip"
        onConnect={this.handleShowCalendarModal}
      >
        <div className="d-flex flex-column gap-1">
          <p className="mt-2 mb-0 fw-semibold">Calendar Sync</p>
          <div className="fs-large text-muted">
            Sync Buildout Calendar Events with your {availableIntegrations} account
          </div>
        </div>
      </AvailableIntegrationsCard>
    );
  }

  renderClickToCallBlock() {
    const { clickToCallIntegration, currentDatabase, currentUser: { clickToCallEnabled }, databases } = this.props;

    if (clickToCallEnabled && clickToCallIntegration == null) {
      return (
        <AvailableIntegrationsCard
          currentDatabase={currentDatabase}
          databases={databases}
          image={clickToCallIcon}
          name="click-to-call"
          toolTipId="click-to-call-tooltip"
          onConnect={this.handleShowClickToCallModal}
        >
          <div className="d-flex flex-column gap-1">
            <p className="mt-2 mb-0 fw-semibold">Click-to-Call</p>
            <div className="fs-large text-muted">
              Connect your phone number to make calls directly from Buildout. Powered by Twilio
            </div>
          </div>
        </AvailableIntegrationsCard>
      );
    }
  }

  renderContactBlock() {
    const { contactIntegration, contactSyncAccess, currentDatabase, databases } = this.props;
    const databaseId = contactIntegration ? contactIntegration.databaseId : null;

    if (!contactSyncAccess || databaseId === currentDatabase.id) return;

    return (
      <AvailableIntegrationsCard
        currentDatabase={currentDatabase}
        databases={databases}
        image={contactIcon}
        integration={contactIntegration}
        name="contacts"
        toolTipId="contact-tooltip"
        onConnect={this.handleShowContactModal}
      >
        <div className="d-flex flex-column gap-1">
          <p className="mt-2 mb-0 fw-semibold">Contact Sync</p>
          <div className="fs-large text-muted">
            Sync Buildout Contact Events with your Google, Office, ICloud or Yahoo account
          </div>
        </div>
      </AvailableIntegrationsCard>
    );
  }

  renderContactModal() {
    const { showContactModal } = this.state;
    const { databases } = this.props;

    return (
      <ContactModal
        databases={databases}
        shown={showContactModal}
        onCancel={this.handleCloseModals}
        onSave={this.handleSaveIntegration}
      />
    );
  }

  renderCalendarModal() {
    const { showCalendarModal } = this.state;
    const { databases } = this.props;

    return (
      <CalendarModal
        databases={databases}
        shown={showCalendarModal}
        onCancel={this.handleCloseModals}
        onSave={this.handleSaveIntegration}
      />
    );
  }

  renderClickToCallModal() {
    const { showClickToCallModal } = this.state;
    const { currentUser } = this.props;

    return (
      <ClickToCallRegistrationModal
        currentUser={currentUser}
        shown={showClickToCallModal}
        onClose={this.handleFinishClickToCall}
      />
    );
  }
}
